import { ReactElement, useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import TableCell from "@mui/material/TableCell";
import { DefaultPagination } from "services";
import Utils from "shared/helpers/Utils";
import { useProgress } from "context/ProgressProvider";
import { LoanService } from "services/LoanService";
import {
  ESignStakeholderStatus,
  StakeholderRole,
} from "shared/helpers/Constants";

export interface SignerListProps {
  name: string;
  email: number;
  phoneNumber: string;
  role: any;
  signedStatus: number;
  requestCompletedTime: Date;
  declineReason: string;
  signRequestId: string;
  stakeholderId: string;
}

export const getEsignSignersStatusColorCode = (
  status: number
): ReactElement => (
  <Box className="status-chip-container">
    {status === ESignStakeholderStatus.REQUESTED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Requested</Typography>
          </>
        }
        className="pending"
        variant="filled"
        size="small"
      />
    )}
    {status === ESignStakeholderStatus.SIGNED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Signed</Typography>
          </>
        }
        className="verified"
        variant="filled"
        size="small"
      />
    )}
    {status === ESignStakeholderStatus.DECLINED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Declined</Typography>
          </>
        }
        className="rejected"
        variant="filled"
        size="small"
      />
    )}
  </Box>
);

const SignersList = (): ReactElement => {
  const { currentProfile } = useProgress();
  const [signers, setSigners] = useState<SignerListProps[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pagination, setPagination] = useState({
    ...DefaultPagination(5),
  });

  // API Calls
  const loadSignersList = (): void => {
    // setLoadingHistory(true);
    const loanId = currentProfile.loadId;
    LoanService.getSignersList(1, 20, loanId)
      .then((response: any) => {
        const { signers, pagination } = response.data.data;
        setSigners(signers);
        setPagination(pagination);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setLoadingHistory(false);
      });
  };

  const actionHandlePageChange = (_event: any, newPage: number): void => {
    pagination.offset = newPage + 1;
    loadSignersList();
  };

  const actionHandleLimitChange = (event: any): void => {
    const rowsPerPage = event.target.value;
    pagination.limit = parseInt(rowsPerPage, 10);
    pagination.offset = 1;
    loadSignersList();
  };

  // Hooks
  useEffect(() => {
    loadSignersList();
  }, []);

  // UI Elements

  const getRoleCode = (roles: number[]): ReactElement => {
    const roleLabels = roles?.map((role: number) => {
      switch (role) {
        case StakeholderRole.DIRECTOR:
          return "Director";
        case StakeholderRole.SHAREHOLDER:
          return "Shareholder";
        case StakeholderRole.EXECUTANT:
          return "Executant";
        default:
          return "";
      }
    });
    return (
      <Typography variant="body1" className="text-bold">
        {roleLabels?.filter(Boolean).join(",  ") || ""}
      </Typography>
    );
  };

  return (
    <Box className="signers-list-wrapper">
      <Typography variant="h2" className="">
        Sign Status
      </Typography>

      <Box className="mt-5 flex-column">
        {loadingHistory && (
          <Box className="empty-content justify-content-center">
            <CircularProgress />
          </Box>
        )}
        {!loadingHistory && (
          <Table className="signers-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signers.map((signer: SignerListProps) => (
                <TableRow>
                  <TableCell>{signer.name}</TableCell>
                  <TableCell>{signer.email}</TableCell>
                  <TableCell>{signer.phoneNumber}</TableCell>
                  <TableCell>{getRoleCode(signer.role)}</TableCell>
                  <TableCell>
                    {getEsignSignersStatusColorCode(signer.signedStatus)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
};

export default SignersList;
